import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { BillingFacade } from '@app/shared/+state/billing';
import { isNotSubscribed, StashedPaymentMethod } from '@app/shared/+state/billing/billing.models';
import { EntitlementEnum, LoadingState } from '@app/shared/interfaces';
import { ConfettiComponent } from '@app/ui/confetti/confetti.component';
import { combineLatest } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'sbnb-manage-subscription-wizard',
  templateUrl: './manage-subscription-wizard.component.html',
  styleUrls: ['./manage-subscription-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class ManageSubscriptionWizardComponent implements OnInit {
  @ViewChild('confetti') confetti: ConfettiComponent;
  // used by "Are you sure?" guard
  public dirty = false;
  public confirmAutomaticResolutions = new UntypedFormControl(false, { validators: [Validators.requiredTrue] });
  public showPaymentMethods = false;
  public showBillingDetails = true;
  public billingDetailsSaved = false;
  public paymentMethodsSaved = false;
  public pendingSubscriptionChange$ = this.billingFacade.pendingSubscriptionChange$.pipe(
    tap(({ submitting }) => {
      if (submitting === LoadingState.Error) {
        this.markClean();
      }
    })
  );

  public submitting$ = this.pendingSubscriptionChange$.pipe(
    map((change) => change.submitting === LoadingState.Pending || change.submitting === LoadingState.Success)
  );

  public success$ = this.pendingSubscriptionChange$.pipe(
    filter((change) => change.submitting === LoadingState.Success),
    switchMap(() => {
      return this.billingFacade.subscriptionLoadingState$.pipe(
        map((state) => state === LoadingState.Success),
        take(1)
      );
    }),
    tap(() => {
      this.confetti.makeItRain();
      this.markClean();
      setTimeout(() => {
        this?.leaveWizard?.();
      }, 15000);
    })
  );

  public newPlan$ = this.billingFacade.pendingSubscriptionChangePlan$;

  /**
   * These selectors use the initial state to build the steps. We don't want the stepper
   * to remove steps as we mute properties or delete secondary users.
   */
  private initialStateOfDowngradeChecks$ = this.pendingSubscriptionChange$.pipe(
    filter((change) => !change.isDowngrade || (change.isDowngrade && Boolean(change.downgradeChecks))),
    map((change) => change.downgradeChecks),
    take(1)
  );

  public initialPropertiesEntitlement$ = this.initialStateOfDowngradeChecks$.pipe(
    map((downgradeChecks) => downgradeChecks?.entitlements?.find((e) => e.entitlement === EntitlementEnum.Properties))
  );
  public initialMaxSecondaryUsersEntitlement$ = this.initialStateOfDowngradeChecks$.pipe(
    map((downgradeChecks) =>
      downgradeChecks?.entitlements?.find((e) => e.entitlement === EntitlementEnum.MaxSecondaryUsers)
    )
  );
  /** End selectors using intial state */

  public automaticallyResolvedSteps$ = this.billingFacade.downgradeChecks$.pipe(
    map((entitlements) => entitlements?.filter((e) => !e.needs_action))
  );

  public mutingComplete = false;
  public deletingUsersComplete = false;
  private currentSubscription$ = this.billingFacade.subscription$;
  private stashedPaymentMethod;

  constructor(
    private readonly billingFacade: BillingFacade,
    private readonly router: Router
  ) { }

  ngOnInit() { }

  public onBillingDetailsSaved(event): void {
    this.billingDetailsSaved = true;
    this.showBillingDetails = false;
    this.showPaymentMethods = true;
    this.markDirty();
  }

  public onPaymentSaved(event: StashedPaymentMethod): void {
    this.paymentMethodsSaved = true;
    this.showPaymentMethods = false;
    this.stashedPaymentMethod = event;
  }

  public resetBillingStep(): void {
    this.billingDetailsSaved = false;
    this.showBillingDetails = true;
    this.showPaymentMethods = false;
    this.paymentMethodsSaved = false;
  }

  public leaveWizard(): void {
    this.markClean();
    this.billingFacade.abandonSubscriptionChange();
    this.router.navigate(['/settings/your-plan']);
  }

  public changeSubscription(): void {
    combineLatest([this.pendingSubscriptionChange$, this.currentSubscription$])
      .pipe(take(1))
      .subscribe(([change, currentSubscription]) => {
        const opts = {
          activeListings: currentSubscription.active_properties,
          stashedPaymentMethod: this.stashedPaymentMethod,
          isSubscribing: isNotSubscribed(currentSubscription),
        };

        this.billingFacade.changeSubscription(change.planId, opts);
      });
  }

  public onMutePropertiesSuccess(stepper: MatStepper) {
    this.markDirty();
    // we need to reload the subscription so that the `active_properties` entry is up-to-date
    //  during the submission process, otherwise the server will kick it back
    this.billingFacade.loadSubscription();
    this.billingFacade.loadDowngradeIssues();
    this.mutingComplete = true;
    stepper?.next();
  }

  public onDeleteUsersSuccess(stepper: MatStepper) {
    this.markDirty();
    this.billingFacade.loadDowngradeIssues();
    this.deletingUsersComplete = true;
    stepper?.next();
  }

  private markDirty() {
    this.dirty = true;
  }

  private markClean() {
    this.dirty = false;
  }
}
